/**
 * Nova agent definitions — shared config for agent types.
 *
 * Single source of truth for agent metadata used by both the sidebar
 * (conversation grouping, new session flow) and the server (validation).
 *
 * Aliases: nova agents, agent config, agent definitions
 */

import type { NovaAgentType } from "./types";

/**
 * The agents that stay free under subscription enforcement — Diagnose is the
 * funnel's free front door. The per-agent gate in `createNovaConversation`/chat
 * enforces this list directly, so it is the boundary that matters, not just
 * the UI. Every agent not listed here is walled. This is the single source of
 * truth for the per-agent paid line: when a deliverable type / agent relocates
 * to Plan, the paid line moves with it automatically because callers derive
 * from this constant rather than hardcoding agent names.
 */
export const FREE_NOVA_AGENTS = ["diagnose"] as const;

/** True when `agentType` names an agent that is free even under enforcement. */
export function isFreeNovaAgent(agentType: NovaAgentType): boolean {
  return (FREE_NOVA_AGENTS as readonly NovaAgentType[]).includes(agentType);
}

export interface NovaAgentConfig {
  type: NovaAgentType;
  name: string;
  shortName: string;
  description: string;
}

export const NOVA_AGENTS: NovaAgentConfig[] = [
  {
    type: "diagnose",
    name: "Diagnose",
    shortName: "Diagnose",
    description:
      "Get an honest read on where your coaching business stands — the foundation your Plan session builds on.",
  },
  {
    type: "plan",
    name: "Plan",
    shortName: "Plan",
    description:
      "Define your ideal client, pressure-test the niche, and shape the offer that sells it.",
  },
  {
    type: "assets",
    name: "Build",
    shortName: "Build",
    description:
      "Turn your strategy into deployment-ready assets — refresh your profile, draft articles, and more. Each asset starts from its card in My assets or My content.",
  },
  {
    type: "grow",
    name: "Grow",
    shortName: "Grow",
    description:
      "Turn your finished offer into a 90-day Client Acquisition System you can run week by week.",
  },
];

export function getAgentConfig(type: NovaAgentType): NovaAgentConfig {
  const config = NOVA_AGENTS.find((a) => a.type === type);
  if (!config) throw new Error(`Unknown agent type: ${type}`);
  return config;
}
